import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLionAutoGroups,
  createLionAutoGroup,
  updateLionAutoGroup,
  reviewLionAutoPending,
  deleteLionAutoGroup,
  matchLionAutoPreview,
  getLionAutoGroupMatches,
  calculateLionAutoLadder,
  getLionAutoStatus,
  updateLionAutoConfig,
  runLionAutoGroup,
  getLionAutoDiscord,
  saveLionAutoDiscord,
  testLionAutoDiscord,
  getLionAutoFloors,
  saveLionAutoFloors,
  followLionAutoFloors,
  getLionAutoSaleRefill,
  saveLionAutoSaleRefill,
  getLionAutoCheckerPreview,
  importLionAutoChecker,
} from '../api';
import { withoutLadder } from '../utils/lionAutoLadder';
import { withPending } from '../utils/lionAutoGroups';
import { shouldRefreshOnReview } from '../utils/lionAutoPending';
import { mergeImportedGroups, shouldRefreshOn, REFRESH_ON_IMPORT } from '../utils/lionAutoChecker';
import { floorsOfSave } from '../utils/lionAutoFloors';

// Event ids come from the route as strings; keys always hold the string so a
// numeric id from an API body can never miss the cache entry
export const lionAutoKeys = {
  all: ['lionAuto'],
  // No id = every event's groups (prefix for invalidation)
  groups: (eventId) => (eventId === undefined ? ['lionAuto', 'groups'] : ['lionAuto', 'groups', String(eventId)]),
  // The event's floors (GET floors); no id = every event's (an artist floor
  // is on every show of the artist)
  floors: (eventId) => (eventId === undefined ? ['lionAuto', 'floors'] : ['lionAuto', 'floors', String(eventId)]),
  // The event's Reprice after a sale (GET sale-refill); no id = every
  // event's (an artist setting and the default are on many shows)
  saleRefill: (eventId) => (eventId === undefined ? ['lionAuto', 'saleRefill'] : ['lionAuto', 'saleRefill', String(eventId)]),
  matches: (eventId) => ['lionAuto', 'matches', String(eventId)],
  // No sig = every preview of the event (prefix for invalidation)
  preview: (eventId, sig) => (sig === undefined
    ? ['lionAuto', 'preview', String(eventId)]
    : ['lionAuto', 'preview', String(eventId), sig]),
  // The scheduled runner: one for the whole server, not per event
  autoStatus: ['lionAuto', 'autoStatus'],
  // The Discord webhook's status (super admin), one for the whole server
  discord: ['lionAuto', 'discord'],
  // The Lion Checker import preview, one per VividSeats link and
  // includeEmpty; no vividUrl = every preview of the event (prefix for
  // invalidation)
  checkerPreview: (eventId, vividUrl, includeEmpty) => (vividUrl === undefined
    ? ['lionAuto', 'checkerPreview', String(eventId)]
    : ['lionAuto', 'checkerPreview', String(eventId), vividUrl || '', includeEmpty ? 1 : 0]),
};

// How often an open Auto panel re-reads the runner's status
export const AUTO_STATUS_POLL_MS = 30 * 1000;

// The runner's status (any staff role; a pricer's groups list only the
// events they can price. DB and memory on the server, no Skybox read).
// poll: re-read every 30 s; react-query skips the interval while the tab is
// hidden (refetchIntervalInBackground stays false) and drops it when the
// last observer unmounts. A 403 / 404 (not staff, or a server without the
// runner yet) is final: no retry, and the poll stops.
export function useLionAutoStatus({ enabled = true, poll = false } = {}) {
  return useQuery({
    queryKey: lionAutoKeys.autoStatus,
    queryFn: async () => (await getLionAutoStatus()).data,
    enabled,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const status = query.state.error?.response?.status;
      if (!poll || status === 403 || status === 404) return false;
      return AUTO_STATUS_POLL_MS;
    },
    retry: (failureCount, err) => {
      const status = err?.response?.status;
      if (status === 403 || status === 404 || status === 400) return false;
      return failureCount < 2;
    },
  });
}

// body: { enabled } / { dryRun } / … → the config object. The status cache
// takes the new config at once, then re-reads (the runner's status follows
// the switch: disabled ↔ idle). Every event's Reprice after a sale re-reads
// too: saleRefill is the default of every show and artist without a setting.
export function useUpdateLionAutoConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await updateLionAutoConfig(body)).data,
    onSuccess: (config) => {
      if (config && typeof config === 'object') {
        queryClient.setQueryData(lionAutoKeys.autoStatus, (old) => (old ? {
          ...old,
          config: { ...(old.config || {}), ...config },
          ...(typeof config.enabled === 'boolean' ? { enabled: config.enabled } : {}),
          ...(typeof config.dryRun === 'boolean' ? { dryRun: config.dryRun } : {}),
        } : old));
      }
      queryClient.invalidateQueries({ queryKey: lionAutoKeys.autoStatus });
      queryClient.invalidateQueries({ queryKey: lionAutoKeys.saleRefill() });
    },
  });
}

// The Discord webhook's status (super admin only; DB and memory on the
// server, never the URL). A 403 / 404 (not a super admin, or a server
// without it yet) is final.
export function useLionAutoDiscord() {
  return useQuery({
    queryKey: lionAutoKeys.discord,
    queryFn: async () => (await getLionAutoDiscord()).data,
    staleTime: 30 * 1000,
    retry: (failureCount, err) => {
      const status = err?.response?.status;
      if (status === 403 || status === 404) return false;
      return failureCount < 2;
    },
  });
}

// variables: the webhook URL, or null to remove it → the status, which the
// cache takes at once before it re-reads
export function useSaveLionAutoDiscord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (webhookUrl) => (await saveLionAutoDiscord(webhookUrl)).data,
    onSuccess: (status) => {
      if (status && typeof status === 'object') queryClient.setQueryData(lionAutoKeys.discord, status);
      queryClient.invalidateQueries({ queryKey: lionAutoKeys.discord });
    },
  });
}

// One test message → { ok, status?, error? }; the status (last sent, last
// error) re-reads either way
export function useTestLionAutoDiscord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => (await testLionAutoDiscord()).data,
    onSettled: () => queryClient.invalidateQueries({ queryKey: lionAutoKeys.discord }),
  });
}

// "Run now" for one group on Auto (variables: the group id) → { group,
// summary }. The group comes back with its new last run, so the groups cache
// takes it at once; the runner's status re-reads. Its writes (a live run)
// change prices only, never membership, so the matches are just marked
// stale, as after a ladder Apply. A 409 / 404 means our copy of the group or
// the runner is stale: both re-read.
export function useRunLionAutoGroup(eventId) {
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.groups(eventId) });
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.autoStatus });
  };
  return useMutation({
    mutationFn: async (id) => (await runLionAutoGroup(id)).data,
    onSuccess: (data) => {
      if (data?.group) {
        queryClient.setQueryData(lionAutoKeys.groups(eventId), (old) => (
          old && Array.isArray(old.groups)
            ? { ...old, groups: old.groups.map(g => (g.id === data.group.id ? data.group : g)) }
            : old
        ));
      }
      queryClient.invalidateQueries({ queryKey: lionAutoKeys.matches(eventId), refetchType: 'none' });
      refresh();
    },
    onError: (err) => {
      const status = err?.response?.status;
      if (status === 409 || status === 404) refresh();
    },
  });
}

// Match calls read the event's Skybox pricing, so they share
// useEventPricing's policy: Skybox blips (503 / timeout) are worth retrying,
// a 404 / 401 / 403 is not, and our own 429 gets exactly one retry after the
// window the server named.
const matchRetry = (failureCount, err) => {
  const status = err?.response?.status;
  if (status === 404 || status === 401 || status === 403) return false;
  if (status === 429) return failureCount < 1;
  return failureCount < 3;
};
const matchRetryDelay = (attempt, err) => {
  if (err?.response?.status === 429) {
    const retryAfter = Number(err?.response?.data?.retryAfter);
    const secs = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 60;
    // +500 ms so we land just past the window boundary, not on it
    return Math.min(secs * 1000 + 500, 65000);
  }
  return Math.min(1000 * 2 ** attempt, 8000);
};

// Keep showing this event's last answer while the next one loads (never
// another event's). Key layout: ['lionAuto', kind, eventId, …].
const sameEventPlaceholder = (eventId) => (prev, prevQuery) =>
  (prevQuery?.queryKey?.[2] === String(eventId) ? prev : undefined);

// Groups are shared by all staff and the page lives in its own tab, so — like
// the notes queries — a short staleTime plus refetch-on-focus keeps them fresh
// across tabs. DB-only on the server, so cheap.
export function useLionAutoGroups(eventId, options = {}) {
  return useQuery({
    queryKey: lionAutoKeys.groups(eventId),
    queryFn: async () => (await getLionAutoGroups(eventId)).data,
    enabled: !!eventId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    ...options,
  });
}

// The event's floors (the artist's and this show's, and what a group with
// no floor of its own inherits). DB-only on the server and shared by all
// staff, so like the groups: a short staleTime plus refetch-on-focus. The
// default retry (none on 401 / 403 / 404 / 429).
export function useLionAutoFloors(eventId, options = {}) {
  return useQuery({
    queryKey: lionAutoKeys.floors(eventId),
    queryFn: async () => (await getLionAutoFloors(eventId)).data,
    enabled: !!eventId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    ...options,
  });
}

// Floor writes touch our DB only and are never retried. The answer's floors
// (the GET shape) go into this event's cache at once, then every event's
// floors re-read: an artist floor is on every show of the artist, open in
// other tabs too. alsoGroups: the write changed groups' own floors (a
// follow), so every event's groups re-read as well (the floor is a ladder
// key, out of groupsMatchSig: no new match); alsoStatus: the write queued
// Auto runs, so the runner's status re-reads. Each is a boolean or a
// function of the variables. A 409 (someone changed a group or a floor
// first) or no answer at all (the outcome is unknown) re-reads the same way.
// Never retried (a mutation's default).
function useFloorsWrite(eventId, mutationFn, floorsOf, { alsoGroups = false, alsoStatus = false } = {}) {
  const queryClient = useQueryClient();
  const refresh = (variables) => {
    const wants = (also) => (typeof also === 'function' ? !!also(variables) : !!also);
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.floors() });
    if (wants(alsoGroups)) queryClient.invalidateQueries({ queryKey: lionAutoKeys.groups() });
    if (wants(alsoStatus)) queryClient.invalidateQueries({ queryKey: lionAutoKeys.autoStatus });
  };
  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      const floors = floorsOf(data);
      if (floors && typeof floors === 'object') queryClient.setQueryData(lionAutoKeys.floors(eventId), floors);
      refresh(variables);
    },
    onError: (err, variables) => {
      const status = err?.response?.status;
      if (!status || status === 409 || status >= 500) refresh(variables);
    },
  });
}

// variables: { artistPct } or { showPct } (null clears), plus applyToAll /
// repriceNow when true → the floors (+ applied / reprice; floorsOfSave keeps
// only the GET shape for the cache). applyToAll cleared groups' own floors:
// every event's groups re-read; repriceNow queued Auto runs: the runner's
// status re-reads.
export function useSaveLionAutoFloors(eventId) {
  return useFloorsWrite(
    eventId,
    async (body) => (await saveLionAutoFloors(eventId, body)).data,
    floorsOfSave,
    { alsoGroups: (body) => body?.applyToAll === true, alsoStatus: (body) => body?.repriceNow === true }
  );
}

// variables: 'show' | 'artist' → { cleared, skipped, floors }
export function useFollowLionAutoFloors(eventId) {
  return useFloorsWrite(
    eventId,
    async (scope) => (await followLionAutoFloors(eventId, scope)).data,
    (data) => data?.floors,
    { alsoGroups: true }
  );
}

// The event's Reprice after a sale (this show's, the artist's, the default and
// what applies). DB-only on the server and shared by all staff, so like the
// floors: a short staleTime plus refetch-on-focus, the default retry.
export function useLionAutoSaleRefill(eventId, options = {}) {
  return useQuery({
    queryKey: lionAutoKeys.saleRefill(eventId),
    queryFn: async () => (await getLionAutoSaleRefill(eventId)).data,
    enabled: !!eventId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    ...options,
  });
}

// variables: { show } or { artist } (true | false | null = the default) →
// the GET shape, which goes into this event's cache at once. Then every
// event's setting re-reads (an artist setting is on every show of the artist,
// open in other tabs too) and so does the runner's status (a group's next
// run can move). Never retried; a 409 (the performer is not known) or no
// answer at all (the outcome is unknown) re-reads the same way.
export function useSaveLionAutoSaleRefill(eventId) {
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.saleRefill() });
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.autoStatus });
  };
  return useMutation({
    mutationFn: async (body) => (await saveLionAutoSaleRefill(eventId, body)).data,
    onSuccess: (view) => {
      if (view && typeof view === 'object') queryClient.setQueryData(lionAutoKeys.saleRefill(eventId), view);
      refresh();
    },
    onError: (err) => {
      const status = err?.response?.status;
      if (!status || status === 409 || status >= 500) refresh();
    },
  });
}

// Short stable hash of everything a match depends on: ids and criteria
// (sections included; not names nor the price ladder's keys, so a rename, a
// new ranking, a new floor or a holding edit costs no Skybox-backed call).
// Members are derived server-side from the live pricing payload, so new or
// sold listings in a group's sections need no group edit to show up.
export function groupsMatchSig(groups = []) {
  const s = JSON.stringify(groups.map(g => [g.id, withoutLadder(g.criteria)]));
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return `${groups.length}:${h.toString(36)}`;
}

// One cached Skybox read matches every saved group. The key carries a hash
// of the groups' match inputs, so a group created or edited here or in
// another tab (seen through the groups refetch) gets a fresh result by
// itself, with no extra invalidation round trip.
export function useLionAutoGroupMatches(eventId, groups = [], options = {}) {
  const sig = groupsMatchSig(groups);
  return useQuery({
    queryKey: [...lionAutoKeys.matches(eventId), sig],
    queryFn: async () => (await getLionAutoGroupMatches(eventId)).data,
    enabled: !!eventId && groups.length > 0,
    staleTime: 5 * 60 * 1000,
    placeholderData: sameEventPlaceholder(eventId),
    retry: matchRetry,
    retryDelay: matchRetryDelay,
    ...options,
  });
}

// What a draft's preview sends and is cached under. draft: { criteria,
// groupId? } — groupId the saved group being edited, none (null) for a new
// group, which goes in the body only when set. A listing of ours is in one
// group per event, so the server matches the draft among the event's other
// saved groups (one of them may hold a listing first): their match inputs
// (groupsMatchSig) are in the sig with the draft's own. The edited group's
// saved criteria are not, since the draft stands in for them. The ladder
// keys (LADDER_KEYS: ranking, offset, step, floor, holding, the no-comp
// step-down rate) are left out — they don't change a match.
export function previewRequest(draft, groups = []) {
  const criteria = draft?.criteria ? withoutLadder(draft.criteria) : null;
  const groupId = draft?.groupId ?? null;
  const others = (Array.isArray(groups) ? groups : [])
    .filter(g => groupId == null || String(g.id) !== String(groupId));
  return {
    body: { criteria, ...(groupId != null ? { groupId } : {}) },
    sig: JSON.stringify([groupId, criteria, groupsMatchSig(others)]),
  };
}

// Live preview of an unsaved group, or of an edit to a saved one. `draft`
// is the DEBOUNCED { criteria, groupId? } (useDebouncedValue), so each pause
// in editing costs one match call; identical drafts hit the cache for a
// minute. `groups` are the event's saved groups: an edit to another one (here
// or in another tab) asks again. The server refuses a match with no
// sections, so none is asked for until one is picked. Ranking listings,
// setting the floor or holding shares the cached answer (previewRequest).
// No retry on 429 — the page toasts it and the next edit asks again.
export function useLionAutoPreview(eventId, draft, groups = [], options = {}) {
  const { body, sig } = previewRequest(draft, groups);
  const hasSections = Array.isArray(body.criteria?.sections) && body.criteria.sections.length > 0;
  return useQuery({
    queryKey: lionAutoKeys.preview(eventId, sig),
    queryFn: async () => (await matchLionAutoPreview(eventId, body)).data,
    enabled: !!eventId && hasSections,
    staleTime: 60 * 1000,
    placeholderData: sameEventPlaceholder(eventId),
    retry: (failureCount, err) => {
      const status = err?.response?.status;
      if (status && status !== 503) return false;
      return failureCount < 2;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    ...options,
  });
}

// The price ladder of the group on screen: { criteria, groupId?, resume? }
// → LadderResult (resume: continue a run that stopped early). Each call is a
// fresh answer (the server asks Skybox's what-if per rung and never writes),
// so nothing is cached here: the page keeps the answer only while the
// criteria it was computed for stay the same. No retry — a 429 is toasted,
// any other failure shown in the bar.
export function useLionAutoLadder(eventId) {
  return useMutation({
    mutationFn: async (body) => (await calculateLionAutoLadder(eventId, body)).data,
  });
}

// Group writes touch our DB only. They invalidate the groups and the saved
// group matches — never eventPricing, which would force a real Skybox read.
// The server's copy of the written group (criteria come back normalized) goes
// into the groups cache at once, so the page can select it before the
// refetch lands; that also changes the matches key, which fetches by itself,
// so older match answers are only marked stale here (no second call).
// A 404 (deleted elsewhere) means our copy of the groups is stale, so it
// refetches too.
// options (a write the matches key can't see, e.g. a pending review: the
// ladder keys are not in groupsMatchSig):
//   refetchMatches  re-read the event's matches now instead of only marking
//                   them stale
//   patchMatches    (matches, body, variables) → matches: the server's answer
//                   (or a refreshOn error's body) into every cached match
//                   answer of the event, before the re-read lands
//   refreshOn       error statuses that mean our copy is stale ([404];
//                   undefined = the server did not answer), or a function
//                   (err) → true when it is (one status, several meanings)
//   invalidateOnSuccess  one more query key to invalidate after a success
const REFRESH_ON_404 = [404];
function useGroupWrite(eventId, mutationFn, patchGroups, {
  refetchMatches = false, patchMatches = null, refreshOn = REFRESH_ON_404, invalidateOnSuccess = null,
} = {}) {
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.groups(eventId) });
    queryClient.invalidateQueries({
      queryKey: lionAutoKeys.matches(eventId),
      ...(refetchMatches ? {} : { refetchType: 'none' }),
    });
  };
  const patch = (body, variables) => {
    if (!patchMatches || !body) return;
    queryClient.setQueriesData({ queryKey: lionAutoKeys.matches(eventId) }, (old) => (
      old ? patchMatches(old, body, variables) : old
    ));
  };
  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(lionAutoKeys.groups(eventId), (old) => (
        old && Array.isArray(old.groups) ? { ...old, groups: patchGroups(old.groups, data, variables) } : old
      ));
      patch(data, variables);
      refresh();
      if (invalidateOnSuccess) queryClient.invalidateQueries({ queryKey: invalidateOnSuccess });
    },
    onError: (err, variables) => {
      const stale = typeof refreshOn === 'function' ? refreshOn(err) : shouldRefreshOn(refreshOn, err);
      if (!stale) return;
      patch(err.response?.data, variables);
      refresh();
    },
  });
}

export function useCreateLionAutoGroup(eventId) {
  return useGroupWrite(
    eventId,
    async (body) => (await createLionAutoGroup(eventId, body)).data,
    (groups, data) => (data?.group ? [...groups.filter(g => g.id !== data.group.id), data.group] : groups)
  );
}

// variables: { id, name?, criteria?, autoEnabled? }. The Auto switch is not
// in groupsMatchSig, so flipping it costs no Skybox-backed match.
export function useUpdateLionAutoGroup(eventId) {
  return useGroupWrite(
    eventId,
    async ({ id, ...body }) => (await updateLionAutoGroup(id, body)).data,
    (groups, data) => (data?.group ? groups.map(g => (g.id === data.group.id ? data.group : g)) : groups)
  );
}

// The group's "needs approval" queue. variables: { id, action: 'approve' |
// 'dismiss', inventoryIds, smart? (approve at the Smart sort spot), beforeId?
// (approve at a rank: just before that ranked listing; neither = ranked
// last) } → { group, pending, approved?, placedLast? | dismissed?, skipped }.
// An approve changes only the group's ladderIds, which groupsMatchSig leaves
// out, so the matches key stays put: the group's new pending list goes into
// the cached matches at once and they re-read (the server's 5-minute pricing
// cache, never eventPricing). A 409 NOT_PENDING (someone else handled them)
// or RANKING_CHANGED (beforeId left the ladder; both bodies carry the current
// pending) and a 404 re-read the same way, but not a 404 PRICING_NOT_FOUND:
// Skybox has no pricing for the event, nothing changed
// (shouldRefreshOnReview).
export function useReviewLionAutoPending(eventId) {
  return useGroupWrite(
    eventId,
    async ({ id, action, inventoryIds, beforeId, smart }) => (await reviewLionAutoPending(id, { action, inventoryIds, beforeId, smart })).data,
    (groups, data) => (data?.group ? groups.map(g => (g.id === data.group.id ? data.group : g)) : groups),
    {
      refetchMatches: true,
      refreshOn: shouldRefreshOnReview,
      patchMatches: (matches, body, { id }) => withPending(matches, id, body?.pending),
    }
  );
}

export function useDeleteLionAutoGroup(eventId) {
  return useGroupWrite(
    eventId,
    async (id) => (await deleteLionAutoGroup(id)).data,
    (groups, _data, id) => groups.filter(g => g.id !== id)
  );
}

// Import from Lion Checker, step 1: the preview (LaCheckerImportModal; it
// only runs while the modal is open). Each opening reads Lion Checker
// afresh and nothing is kept after it closes (staleTime / gcTime 0). No
// refetch on focus and no retry: each read costs a Lion Checker query and a
// Skybox pricing read (the server's 5-minute cache), and the modal has Retry.
// includeEmpty: the filters that match none of our listings come back as
// creates instead of skipped NO_LISTINGS. Flipping it keeps the last answer
// (and its own includeEmpty, which the server echoes) on screen until the
// new one lands; a failed read shows no placeholder.
export function useLionAutoCheckerPreview(eventId, { enabled = true, vividUrl = null, includeEmpty = false } = {}) {
  return useQuery({
    queryKey: lionAutoKeys.checkerPreview(eventId, vividUrl || '', includeEmpty),
    queryFn: async () => (await getLionAutoCheckerPreview(eventId, { vividUrl, includeEmpty })).data,
    enabled: !!eventId && enabled,
    staleTime: 0,
    gcTime: 0,
    placeholderData: sameEventPlaceholder(eventId),
    refetchOnWindowFocus: false,
    retry: false,
  });
}

// Step 2: variables { selections: [{ checkerEventId?, filterIndex, sig }],
// vividUrl?, includeEmpty? (the preview's, so the server decides the same) }
// → { created, updated, skipped }. The groups cache takes the created and
// updated groups at once; an import changes groups' sections, rows or Qty,
// so the matches re-read now, and so does the preview (what was imported
// now reads "Same"). A 409 CHECKER_STALE (a filter changed on Lion Checker
// since the preview), a 404, a 500 or no answer re-reads the groups and
// matches (REFRESH_ON_IMPORT): after a 500 or no answer the outcome is
// unknown, the batch may have committed. The modal reloads the preview when
// the pricer asks. Never retried.
export function useImportLionAutoChecker(eventId) {
  return useGroupWrite(
    eventId,
    async ({ selections, vividUrl, includeEmpty }) =>
      (await importLionAutoChecker(eventId, { selections, vividUrl, includeEmpty })).data,
    mergeImportedGroups,
    {
      refetchMatches: true,
      refreshOn: REFRESH_ON_IMPORT,
      invalidateOnSuccess: lionAutoKeys.checkerPreview(eventId),
    }
  );
}
